import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
// Import analytics components from correct path
import MembershipAnalytics from '../../components/analytics/MembershipAnalytics';
import FinancialAnalytics from '../../components/analytics/FinancialAnalytics';
import EngagementAnalytics from '../../components/analytics/EngagementAnalytics';
import CustomerSuccessAnalytics from '../../components/analytics/CustomerSuccessAnalytics';
import AnalyticsSummary from '../../components/analytics/AnalyticsSummary';
import { analyticsService } from '../../services/analyticsService';
import tenantService from '../../services/tenantService';
import RoleBasedSidebar from '../../components/RoleBasedSidebar';

// Import Material UI components
import {
  Box, Paper, Typography, CircularProgress, Alert, 
  FormControl, InputLabel, MenuItem, Select, Button,
  Grid, Tabs, Tab
} from '@mui/material';
// Import Material UI icons
import {
  Assessment as AssessmentIcon,
  Business as BusinessIcon
} from '@mui/icons-material';

const AnalyticsPage = () => {
  const { user } = useAuth();
  const { tenant, tenants, loading: tenantLoading, setCurrentTenant } = useTenant();
  const [selectedTenantId, setSelectedTenantId] = useState('');
  
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [timeframe, setTimeframe] = useState('1y');
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState(null);
  
  // Set selected tenant ID when tenant changes
  useEffect(() => {
    if (tenant?._id) {
      setSelectedTenantId(tenant._id);
      localStorage.setItem('tenantId', tenant._id);
    }
  }, [tenant]);

  // Handle tenant selection change
  const handleTenantChange = (event) => {
    setSelectedTenantId(event.target.value);
  };

  // Handle tenant selection confirmation
  const handleSelectTenant = () => {
    if (!selectedTenantId) return;
    
    // Add null check for tenants array
    const newTenant = tenants?.find(t => t._id === selectedTenantId);
    if (newTenant) {
      setCurrentTenant(newTenant);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Make sure tenant ID is available
        const tenant = tenantService.getCurrentTenant();
        const tenantId = tenant?._id || tenant?.id || localStorage.getItem(tenantService.TENANT_ID_STORAGE_KEY);
        
        if (!tenantId) {
          console.error('No tenant ID available for analytics');
          setError('Tenant information is missing. Please select a tenant and try again.');
          setLoading(false);
          return;
        }
        
        console.log(`Fetching analytics data with tenant ID: ${tenantId}`);
        
        try {
          // Fetch all analytics data in parallel
          const [dashboardData, membershipData, financialData, engagementData, customerExpData] = await Promise.all([
            analyticsService.getDashboardAnalytics(),
            analyticsService.getMembershipAnalytics(timeframe),
            analyticsService.getFinancialAnalytics(timeframe),
            analyticsService.getEngagementAnalytics(),
            analyticsService.getCustomerExperienceAnalytics(timeframe)
          ]);

          // Check if we have valid data with success flag or use fallbacks
          const data = {
            dashboard: dashboardData?.success ? dashboardData.data : {},
            membership: membershipData?.success ? membershipData.data : {},
            financial: financialData?.success ? financialData.data : {},
            engagement: engagementData?.success ? engagementData.data : {},
            customerExperience: customerExpData?.success ? customerExpData.data : {}
          };
          
          setAnalyticsData(data);
        } catch (apiError) {
          console.error('API error fetching analytics:', apiError);
          setError('Error loading analytics data. Using sample data.');
          
          // Use fallback data from service
          const fallbackData = {
            dashboard: analyticsService.getDefaultAnalytics(),
            membership: { growth: [], distribution: [], retention: 0 },
            financial: { revenue: [], transactions: [], summary: {} },
            engagement: { activity: [], satisfaction: 0, nps: 0 },
            customerExperience: analyticsService.getDefaultCustomerExperienceData()
          };
          
          setAnalyticsData(fallbackData);
        }
      } catch (error) {
        console.error('Error in analytics dashboard:', error);
        setError('Error loading analytics. Using sample data.');
        
        // Set default data structure to prevent errors
        setAnalyticsData({
          dashboard: analyticsService.getDefaultAnalytics(),
          membership: { growth: [], distribution: [], retention: 0 },
          financial: { revenue: [], transactions: [], summary: {} },
          engagement: { activity: [], satisfaction: 0, nps: 0 },
          customerExperience: analyticsService.getDefaultCustomerExperienceData()
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeframe]);

  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Early UI states for loading and no tenant
  if (tenantLoading) {
    return (
      <RoleBasedSidebar>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <CircularProgress />
        </Box>
      </RoleBasedSidebar>
    );
  }

  if (!tenant) {
    return (
      <RoleBasedSidebar>
        <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 5 }}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3 }}>
            <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
              <BusinessIcon sx={{ mr: 1 }} /> Select a Tenant
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              Please select a tenant to view analytics
            </Alert>
            
            {!tenants || tenants.length === 0 ? (
              <Alert severity="warning" sx={{ mb: 3 }}>
                No tenants available. Please contact your administrator.
              </Alert>
            ) : (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="tenant-select-label">Tenant</InputLabel>
                <Select
                  labelId="tenant-select-label"
                  id="tenant-select"
                  value={selectedTenantId}
                  label="Tenant"
                  onChange={handleTenantChange}
                >
                  {tenants.map((t) => (
                    <MenuItem key={t._id} value={t._id}>{t.name || t.tenantName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            <Button 
              variant="contained" 
              color="primary" 
              disabled={!selectedTenantId}
              onClick={handleSelectTenant}
              fullWidth
            >
              Continue
            </Button>
            
            {/* Debug button - only in development */}
            {process.env.NODE_ENV === 'development' && (
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => {
                  // Set a mock tenant for development purposes
                  const mockTenant = {
                    _id: '67e9b1794d4dd4221be503b6',
                    name: 'Demo Organization',
                    tenantName: 'Demo Organization',
                    email: 'admin@demo.com'
                  };
                  
                  setCurrentTenant(mockTenant);
                  localStorage.setItem('tenantId', mockTenant._id);
                  console.log('Set mock tenant for development:', mockTenant);
                }}
              >
                Use Demo Tenant (Dev Only)
              </Button>
            )}
          </Paper>
        </Box>
      </RoleBasedSidebar>
    );
  }

  return (
    <RoleBasedSidebar>
      {/* Header Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 4, 
          p: 3, 
          borderRadius: 2,
          background: 'linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)',
          color: 'white'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
            <AssessmentIcon sx={{ mr: 1 }} /> Analytics
          </Typography>
          
          <FormControl sx={{ minWidth: 200, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 1 }}>
            <Select
              value={selectedTenantId}
              onChange={handleTenantChange}
              onClose={handleSelectTenant}
              displayEmpty
              variant="outlined"
              sx={{ 
                color: 'white',
                '.MuiOutlinedInput-notchedOutline': { 
                  borderColor: 'rgba(255,255,255,0.3)' 
                },
                '&:hover .MuiOutlinedInput-notchedOutline': { 
                  borderColor: 'rgba(255,255,255,0.5)' 
                },
                '.MuiSvgIcon-root': {
                  color: 'white'
                }
              }}
            >
              {tenants?.map((t) => (
                <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
              )) || <MenuItem disabled>No tenants available</MenuItem>}
            </Select>
          </FormControl>
        </Box>
        <Typography variant="body1" sx={{ opacity: 0.8 }}>
          Comprehensive analytics for {tenant?.name || 'your organization'}'s performance
        </Typography>
      </Paper>

      {/* Analytics Dashboard Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ p: 3 }}>
          <Alert 
            severity="warning"
            sx={{ mb: 3 }}
          >
            {error}
          </Alert>
          {analyticsData && <AnalyticsSummary data={analyticsData} />}
        </Box>
      ) : !analyticsData || !Object.keys(analyticsData).length ? (
        <Box sx={{ p: 3 }}>
          <Alert severity="warning">
            No analytics data available. Please try again later.
          </Alert>
        </Box>
      ) : (
        <Box>
          {/* Analytics Summary Cards */}
          <AnalyticsSummary data={analyticsData} />
          
          {/* Analytics Tabs */}
          <Paper sx={{ mt: 3, borderRadius: 2 }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant="fullWidth"
              indicatorColor="primary"
              textColor="primary"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Membership" />
              <Tab label="Financial" />
              <Tab label="Customer Success" />
              <Tab label="Engagement" />
            </Tabs>
            
            <Box sx={{ p: 3 }}>
              {tabValue === 0 && (
                <MembershipAnalytics data={analyticsData.membership} onTimeframeChange={handleTimeframeChange} timeframe={timeframe} />
              )}
              {tabValue === 1 && (
                <FinancialAnalytics data={analyticsData.financial} onTimeframeChange={handleTimeframeChange} timeframe={timeframe} />
              )}
              {tabValue === 2 && (
                <CustomerSuccessAnalytics data={analyticsData} onTimeframeChange={handleTimeframeChange} timeframe={timeframe} />
              )}
              {tabValue === 3 && (
                <EngagementAnalytics data={analyticsData.engagement} />
              )}
            </Box>
          </Paper>
        </Box>
      )}
    </RoleBasedSidebar>
  );
};

export default AnalyticsPage;